import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import concessionFeeLogo from "../../../../assets/images/concession-fee-logo.svg";
import CommercialAirlineNavigationBar from "../../../../components/element/CommercialAirlineNavigationBar";
import useAirlineAdminApi from "../../../../services/api/useAirlineAdminApi";
import { COMMERCIAL_INVOICE_STATUS, INVOICE_BREADCRUMB_SCREEN_NAME } from "../../../../utils/constants";
import ROUTE_PATHS from "../../../../utils/routePaths";
import ConcessionFeeTable from "../../Concession Fee/Tables/ConcessionFeeTable";
import { ExportOutlined } from "@ant-design/icons";
import SendCommercialInvoiceDialog from "../InvoiceDetails/views/SendCommercialInvoiceDialog";
import "../InvoiceDetails/InvoiceDetail.css";
import SnackbarAlert from "../../../../components/element/SnackbarAlert";

const ConcessionFeeDetails = () => {
  const location = useLocation();
  const { fetchAirlineVendorLease, exportVendorLeaseInvoiceDetails } = useAirlineAdminApi();
  const [concessionFeeData, setConcessionFeeData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const getStateData = () => {
    if (location.state) {
      sessionStorage.setItem(
        "invoiceDetailData",
        JSON.stringify(location.state)
      );
      return location.state;
    }
    const storedData = sessionStorage.getItem("invoiceDetailData");
    return storedData ? JSON.parse(storedData) : null;
  };

  const stateData = getStateData();
  const invoiceData = stateData?.invoiceData;

  const leaseId = invoiceData?.id;
  const {
    data: airlineVendorLeaseData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["airline-vendor-lease", leaseId, currentPage, pageSize],
    queryFn: () => fetchAirlineVendorLease(leaseId, currentPage, pageSize),
    enabled: !!leaseId,
    retry: false,
  });

  useEffect(() => {
    if (airlineVendorLeaseData?.data) {
      setConcessionFeeData(airlineVendorLeaseData.data.content || []);
    }
  }, [airlineVendorLeaseData]);

  const getBreadcrumbScreenName = (invoiceStatus) => {
    if (!invoiceStatus) return "";

    const statusArray = invoiceStatus.split(",");
    if (statusArray.includes(COMMERCIAL_INVOICE_STATUS.PAID_INVOICE)) {
      return INVOICE_BREADCRUMB_SCREEN_NAME.PAID_INVOICE;
    }
    if (statusArray.includes(COMMERCIAL_INVOICE_STATUS.SETTLED_INVOICE)) {
      return INVOICE_BREADCRUMB_SCREEN_NAME.SETTLED_INVOICE;
    }

    if (statusArray.includes(COMMERCIAL_INVOICE_STATUS.OVERDUE_INVOICE)) {
      return INVOICE_BREADCRUMB_SCREEN_NAME.OVERDUE_INVOICE;
    }

    if (statusArray.includes(COMMERCIAL_INVOICE_STATUS.PARTIAL_PAYMENT)) {
      return INVOICE_BREADCRUMB_SCREEN_NAME.NEW_INVOICE;
    }

    return INVOICE_BREADCRUMB_SCREEN_NAME[invoiceStatus] || "";
  };

  const invoiceBreadcrumbScreenName = getBreadcrumbScreenName(
    invoiceData?.invoiceStatus
  );

  const exportVendorLeaseMutation = useMutation({
    mutationFn: () => exportVendorLeaseInvoiceDetails(invoiceData?.id),
    onSuccess: () => {
      setShowExportDialog(false);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 4000);
    },
  });

  return (
    <div>
      <div className="concession-fee-container">
        <CommercialAirlineNavigationBar
          title={`Invoices for ${invoiceData?.dynamicsInvoiceNumber || ""}`}
          navigationLogo={concessionFeeLogo}
          fullWidthContent={true}
          backContent={
            <div className="navigation-header">
              <Link
                to={ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME}
                className="navigation-header-link"
              >
                Home/
              </Link>
              <Link
                to={ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE}
                className="navigation-header-link"
                onClick={() => sessionStorage.removeItem("invoiceDetailData")}
              >
                Invoices/{invoiceBreadcrumbScreenName}/
              </Link>
              {`Invoices for ${invoiceData?.dynamicsInvoiceNumber || ""}`}
            </div>
          }
        />
      </div>
      {concessionFeeData && concessionFeeData.length ? (
        <div className="invoice-export-button-container">
          <div
            className="invoice-export-button"
            onClick={() => setShowExportDialog(true)}
          >
            <ExportOutlined className="invoice-export-button-icon" />
            Export
          </div>
        </div>
      ) : null}
      <ConcessionFeeTable
        concessionFeeData={concessionFeeData}
        isLoading={isLoading}
        error={error}
      />
      <SendCommercialInvoiceDialog
        open={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        onClickSend={() => exportVendorLeaseMutation.mutate()}
        isLoading={exportVendorLeaseMutation.isPending}
        showMessage={
          "Do you want to send the invoice flight details report to your registered email address?"
        }
        buttonText="Send"
        title="Send Invoice Flight Details Report"
      />

      {!exportVendorLeaseMutation.isPending && exportVendorLeaseMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {exportVendorLeaseMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}

      {showSuccessMessage && (
        <SnackbarAlert type="success">
          The invoice details are being generated and will be sent to your email once completed.
        </SnackbarAlert>
      )}
    </div>
  );
};

export default ConcessionFeeDetails;
